'use client';

import { c as _c } from "react/compiler-runtime";
import { AiSpendOverTimeCard } from '@/domains/pages/ai-spend/components/ai-spend-over-time-card';
import { AvgSpendPerTaskCard } from '@/domains/pages/ai-spend/components/avg-spend-per-task-card';
import { SpendByModelCard } from '@/domains/pages/ai-spend/components/spend-by-model-card';
import { SpendDistributionCard } from '@/domains/pages/ai-spend/components/spend-distribution-card';
import { SpendBreakdownDrawer } from '@/domains/pages/ai-spend/components/spend-breakdown-drawer';
import { TotalAiSpendCard } from '@/domains/pages/ai-spend/components/total-ai-spend-card';
import { AI_SPEND_KPI_GRID_CLASS, type SpendChartMode, type SpendGroupKind } from '@/domains/pages/ai-spend/constants';
import { useAiSpendDistributionData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-distribution-data';
import { useAiSpendTaskModelData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-task-model-data';
import { parseSpendChartMode } from '@/domains/pages/ai-spend/utils/ai-spend-over-time.utils';
import { useAiSpendPageData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-page-data';
import { useSyncNebulyPageViewState } from '@/domains/pages/hooks/use-sync-nebuly-page-view-state';
import type { NebulyPageBodyProps } from '@/domains/pages/types';
import { PARAMS_KEY_SPEND_CHART, PARAMS_KEY_SPEND_GROUP } from '@/constants/route-params-keys';
import { useCreateQueryStringUrl } from '@/hooks/use-create-query-string-url';
import { useProjectsContext } from '@/providers/projects-provider';
import { cn } from '@/utils/shad';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
function parseSpendGroup(value: string | null): SpendGroupKind {
  if (value === 'task' || value === 'model' || value === 'team' || value === 'market' || value === 'tenure') {
    return value;
  }
  return 'task';
}
export function AiSpendPageBody(t0) {
  const $ = _c(97);
  const {
    pageKey,
    timeRange,
    filters: t1,
    specialFilters: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const filters = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const specialFilters = t4;
  const {
    projectId
  } = useProjectsContext();
  const searchParams = useSearchParams();
  const router = useRouter();
  const createQueryStringUrl = useCreateQueryStringUrl();
  let t5;
  if ($[4] !== searchParams) {
    t5 = parseSpendGroup(searchParams.get(PARAMS_KEY_SPEND_GROUP));
    $[4] = searchParams;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const spendGroup = t5;
  let t6;
  if ($[6] !== searchParams) {
    t6 = parseSpendChartMode(searchParams.get(PARAMS_KEY_SPEND_CHART));
    $[6] = searchParams;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const spendChart = t6;
  let t7;
  if ($[8] !== filters || $[9] !== projectId || $[10] !== specialFilters || $[11] !== timeRange) {
    t7 = {
      projectId,
      timeRange,
      filters,
      specialFilters,
      enabled: true
    };
    $[8] = filters;
    $[9] = projectId;
    $[10] = specialFilters;
    $[11] = timeRange;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const allData = useAiSpendPageData(t7);
  let t8;
  if ($[13] !== allData.org.totalCost || $[14] !== filters || $[15] !== projectId || $[16] !== specialFilters || $[17] !== spendGroup || $[18] !== timeRange) {
    t8 = {
      projectId,
      timeRange,
      filters,
      specialFilters,
      kind: spendGroup,
      totalCost: allData.org.totalCost
    };
    $[13] = allData.org.totalCost;
    $[14] = filters;
    $[15] = projectId;
    $[16] = specialFilters;
    $[17] = spendGroup;
    $[18] = timeRange;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const distributionData = useAiSpendDistributionData(t8);
  let t9;
  if ($[20] !== filters || $[21] !== projectId || $[22] !== specialFilters || $[23] !== timeRange) {
    t9 = {
      projectId,
      timeRange,
      filters,
      specialFilters
    };
    $[20] = filters;
    $[21] = projectId;
    $[22] = specialFilters;
    $[23] = timeRange;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const taskModelData = useAiSpendTaskModelData(t9);
  const [costPerTaskHoveredKey, setCostPerTaskHoveredKey] = useState(null);
  const [totalBreakdownOpen, setTotalBreakdownOpen] = useState(false);
  const [perTaskBreakdownOpen, setPerTaskBreakdownOpen] = useState(false);
  let t10;
  if ($[25] !== createQueryStringUrl || $[26] !== router) {
    t10 = nextKind => {
      router.replace(createQueryStringUrl({
        params: {
          [PARAMS_KEY_SPEND_GROUP]: nextKind
        }
      }));
    };
    $[25] = createQueryStringUrl;
    $[26] = router;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleSpendGroupChange = t10;
  let t11;
  if ($[28] !== createQueryStringUrl || $[29] !== router) {
    t11 = nextMode => {
      if (nextMode === "total") {
        router.replace(createQueryStringUrl({
          params: {},
          toRemoveParams: [PARAMS_KEY_SPEND_CHART]
        }));
        return;
      }
      router.replace(createQueryStringUrl({
        params: {
          [PARAMS_KEY_SPEND_CHART]: nextMode
        }
      }));
    };
    $[28] = createQueryStringUrl;
    $[29] = router;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleSpendChartChange = t11;
  const isAllEmpty = !allData.isLoading && !allData.isError && allData.org.tasks === 0 && (allData.org.totalCost == null || allData.org.totalCost === 0);
  let t12;
  if ($[31] !== allData.error || $[32] !== allData.isError || $[33] !== allData.isLoading || $[34] !== allData.refetch || $[35] !== isAllEmpty) {
    t12 = {
      isLoading: allData.isLoading,
      isError: allData.isError,
      error: allData.error,
      refetch: allData.refetch,
      isEmpty: isAllEmpty
    };
    $[31] = allData.error;
    $[32] = allData.isError;
    $[33] = allData.isLoading;
    $[34] = allData.refetch;
    $[35] = isAllEmpty;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  useSyncNebulyPageViewState(t12);
  if (allData.isLoading || allData.isError || isAllEmpty) {
    return null;
  }
  const t13 = `nebuly-page-body-${pageKey}`;
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn(AI_SPEND_KPI_GRID_CLASS, "items-stretch border-b border-b-lightborder");
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setTotalBreakdownOpen(true);
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== allData.org || $[40] !== timeRange) {
    t16 = <TotalAiSpendCard org={allData.org} timeRange={timeRange} onOpenBreakdown={t15} className="grow-0 border-b border-b-lightborder" />;
    $[39] = allData.org;
    $[40] = timeRange;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setPerTaskBreakdownOpen(true);
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== allData.org || $[44] !== allData.teamsForCostPerTask || $[45] !== costPerTaskHoveredKey) {
    t18 = <AvgSpendPerTaskCard org={allData.org} teams={allData.teamsForCostPerTask} onOpenDistribution={t17} hoveredKey={costPerTaskHoveredKey} onHoverChange={setCostPerTaskHoveredKey} className="grow-0" />;
    $[43] = allData.org;
    $[44] = allData.teamsForCostPerTask;
    $[45] = costPerTaskHoveredKey;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t16 || $[48] !== t18) {
    t19 = <div className="flex min-w-0 flex-col border-r border-r-lightborder">{t16}{t18}</div>;
    $[47] = t16;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== allData.peerBand || $[51] !== allData.spendTrend.labels || $[52] !== allData.spendTrend.spendPerTask || $[53] !== allData.spendTrend.totalSpend || $[54] !== handleSpendChartChange || $[55] !== spendChart) {
    t20 = <div className="flex h-full min-w-0"><AiSpendOverTimeCard mode={spendChart} onModeChange={handleSpendChartChange} labels={allData.spendTrend.labels} totalSpend={allData.spendTrend.totalSpend} spendPerTask={allData.spendTrend.spendPerTask} peerBand={allData.peerBand} segmentNoun="team" testId="ai-spend-over-time-card" /></div>;
    $[50] = allData.peerBand;
    $[51] = allData.spendTrend.labels;
    $[52] = allData.spendTrend.spendPerTask;
    $[53] = allData.spendTrend.totalSpend;
    $[54] = handleSpendChartChange;
    $[55] = spendChart;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  let t21;
  if ($[57] !== t19 || $[58] !== t20) {
    t21 = <div className={t14}>{t19}{t20}</div>;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== distributionData.compositionSegments || $[61] !== distributionData.isError || $[62] !== distributionData.isLoading || $[63] !== distributionData.refetch || $[64] !== distributionData.rows || $[65] !== handleSpendGroupChange || $[66] !== spendGroup) {
    t22 = <SpendDistributionCard kind={spendGroup} onKindChange={handleSpendGroupChange} rows={distributionData.rows} compositionSegments={distributionData.compositionSegments} isLoading={distributionData.isLoading} isError={distributionData.isError} onRetry={distributionData.refetch} />;
    $[60] = distributionData.compositionSegments;
    $[61] = distributionData.isError;
    $[62] = distributionData.isLoading;
    $[63] = distributionData.refetch;
    $[64] = distributionData.rows;
    $[65] = handleSpendGroupChange;
    $[66] = spendGroup;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== taskModelData.isError || $[69] !== taskModelData.isLoading || $[70] !== taskModelData.refetch || $[71] !== taskModelData.series || $[72] !== taskModelData.taskLabels) {
    t23 = <SpendByModelCard taskLabels={taskModelData.taskLabels} series={taskModelData.series} isLoading={taskModelData.isLoading} isError={taskModelData.isError} onRetry={taskModelData.refetch} />;
    $[68] = taskModelData.isError;
    $[69] = taskModelData.isLoading;
    $[70] = taskModelData.refetch;
    $[71] = taskModelData.series;
    $[72] = taskModelData.taskLabels;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  let t24;
  if ($[74] !== allData.org.spendPerTask || $[75] !== allData.org.totalCost || $[76] !== filters || $[77] !== projectId || $[78] !== specialFilters || $[79] !== timeRange || $[80] !== totalBreakdownOpen) {
    t24 = <SpendBreakdownDrawer open={totalBreakdownOpen} onOpenChange={setTotalBreakdownOpen} metric="total" orgTotalCost={allData.org.totalCost} orgSpendPerTask={allData.org.spendPerTask} timeRange={timeRange} projectId={projectId} filters={filters} specialFilters={specialFilters} />;
    $[74] = allData.org.spendPerTask;
    $[75] = allData.org.totalCost;
    $[76] = filters;
    $[77] = projectId;
    $[78] = specialFilters;
    $[79] = timeRange;
    $[80] = totalBreakdownOpen;
    $[81] = t24;
  } else {
    t24 = $[81];
  }
  let t25;
  if ($[82] !== allData.org.spendPerTask || $[83] !== allData.org.totalCost || $[84] !== filters || $[85] !== perTaskBreakdownOpen || $[86] !== projectId || $[87] !== specialFilters || $[88] !== timeRange) {
    t25 = <SpendBreakdownDrawer open={perTaskBreakdownOpen} onOpenChange={setPerTaskBreakdownOpen} metric="perTask" orgTotalCost={allData.org.totalCost} orgSpendPerTask={allData.org.spendPerTask} timeRange={timeRange} projectId={projectId} filters={filters} specialFilters={specialFilters} />;
    $[82] = allData.org.spendPerTask;
    $[83] = allData.org.totalCost;
    $[84] = filters;
    $[85] = perTaskBreakdownOpen;
    $[86] = projectId;
    $[87] = specialFilters;
    $[88] = timeRange;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== t13 || $[91] !== t21 || $[92] !== t22 || $[93] !== t23 || $[94] !== t24 || $[95] !== t25) {
    t26 = <div className="border-b border-b-lightborder" data-testid={t13}>{t21}{t22}{t23}{t24}{t25}</div>;
    $[90] = t13;
    $[91] = t21;
    $[92] = t22;
    $[93] = t23;
    $[94] = t24;
    $[95] = t25;
    $[96] = t26;
  } else {
    t26 = $[96];
  }
  return t26;
}